function hasPositiveFactor(currency) {
    return currency?.factor > 0;
}

// Consumers convert a source amount with `amount / relativeFactor`.
// Returns null when no conversion applies (missing/same currency or a non-positive factor).
export function getRelativeFactor(sourceCurrency, targetCurrency) {
    if (!hasPositiveFactor(sourceCurrency) || !hasPositiveFactor(targetCurrency)) {
        return null;
    }

    if (sourceCurrency.currencyID === targetCurrency.currencyID) {
        return null;
    }

    return targetCurrency.factor / sourceCurrency.factor;
}

// The target currency's commission makes the converted amount larger, so it shrinks the factor.
export function applyCommissionToFactor(relativeFactor, commissionRate) {
    return relativeFactor / (1 + (commissionRate || 0));
}

// Falls back to the default currency when no source is given.
// Returns null when there is nothing to convert (see getRelativeFactor).
export function buildDisplayCurrency(currencies, targetCurrencyId, sourceCurrencyId) {
    const resolvedSourceCurrencyId = sourceCurrencyId || currencies.find(currency => currency.isDefault)?.currencyID;
    const targetCurrency = currencies.find(currency => currency.currencyID === targetCurrencyId);
    const sourceCurrency = currencies.find(currency => currency.currencyID === resolvedSourceCurrencyId);
    const relativeFactor = getRelativeFactor(sourceCurrency, targetCurrency);

    if (relativeFactor === null) {
        return null;
    }

    return {
        ...targetCurrency,
        relativeToCurrencyID: sourceCurrency.currencyID,
        relativeFactor,
        relativeFactorWithCommission: applyCommissionToFactor(relativeFactor, targetCurrency.commissionRate),
    };
}

export function convertAmount(amount, sourceCurrency, targetCurrency, includeCommission = false) {
    const relativeFactor = getRelativeFactor(sourceCurrency, targetCurrency);

    if (relativeFactor === null) {
        return amount;
    }

    return amount / (includeCommission
        ? applyCommissionToFactor(relativeFactor, targetCurrency.commissionRate)
        : relativeFactor);
}
